import React, { useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import _omit from 'lodash/omit'
import { selectIsTouchable } from '../../services/app/selectors'
import Icon from '../Icon/index'
import Link from '../Link/index'
import { parse as parseQuery } from '../../services/query-string/index'
import { setAiGa4Event } from '../../services/event-tracking/actions'
import { AI_CONVERSATION_OPENED_GA4_EVENT } from '../../services/event-tracking/index'
import { setOraclePersona } from '../../services/oracle/actions'
import { URL_AI_CHAT } from '../../services/url/constants'
import { usePostHogEvent } from '../PostHog/index'
import { makeIcon } from '../OracleCommons/icons'
import { PersonalityIcon } from './PersonalityIcon'
import { Location } from './history-types'
import { InternationalStrings } from '../OracleCommons/StaticTextContext'

interface OracleSidebarItemProps {
  title: string
  titleHtml?: string
  isSelected?: boolean
  isContextMenuActive?: boolean
  onSelect?: () => void
  onContextMenu?: (triggerElement: HTMLElement) => void
  className?: string
  staticText?: InternationalStrings
  location: Location
  chatSessionId?: string
  personalityId?: string
  personalityKey?: string
  renameState?: {
    isRenaming: boolean
    renamingSessionId: string | null
    newName: string
  }
  onStartRename?: (sessionId: string, currentName: string) => void
  onCancelRename?: () => void
  onSaveRename?: () => void
}

export const OracleSidebarItem: React.FC<OracleSidebarItemProps> = ({
  title,
  titleHtml,
  isSelected = false,
  isContextMenuActive = false,
  onSelect,
  onContextMenu,
  className = '',
  staticText,
  location,
  chatSessionId,
  personalityId,
  renameState,
  onStartRename,
  onCancelRename,
  onSaveRename,
}) => {
  const itemRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const dispatch = useDispatch()
  const isTouchable = useSelector(selectIsTouchable)
  const trackConversationOpened = usePostHogEvent('ai_conversation_opened')
  // Parse current query parameters and create new ones with chatSessionId
  const currentQuery = parseQuery(location.search)
  const newQuery = chatSessionId
    ? {
      ..._omit(currentQuery, ['oraclePersonalityId', 'scrollTo', 'pos', 'snippet', 'matchSource', 'searchActive']),
      chatSessionId,
    }
    : currentQuery

  const handleClick = () => {
    if (onSelect) {
      onSelect()
    }

    // Set Personality
    dispatch(setOraclePersona(personalityId))

    // AI GA4 event
    dispatch(setAiGa4Event(AI_CONVERSATION_OPENED_GA4_EVENT))
    trackConversationOpened()
  }

  const handleContextMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (onContextMenu) {
      const triggerElement = e.currentTarget as HTMLElement
      onContextMenu(triggerElement)
    }
  }

  const handleContextMenuTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation()
  }

  const handleContextMenuTouchEnd = (e: React.TouchEvent) => {
    e.stopPropagation()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleClick()
    }
  }

  // Rename functionality
  const isRenaming = renameState?.isRenaming && renameState?.renamingSessionId === chatSessionId

  // Focus the input when renaming starts
  useEffect(() => {
    if (isRenaming && inputRef.current) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus()
          inputRef.current.select()
        }
      }, 0)
    }
  }, [isRenaming])

  const handleRenameInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onStartRename && chatSessionId) {
      // Enforce maxLength in JavaScript as well
      const value = e.target.value.slice(0, 100)
      onStartRename(chatSessionId, value)
    }
  }

  const handleRenameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (onSaveRename) {
        onSaveRename()
      }
    } else if (e.key === 'Escape') {
      e.preventDefault()
      if (onCancelRename) {
        onCancelRename()
      }
    }
  }

  const handleRenameBlur = () => {
    if (onSaveRename) {
      onSaveRename()
    }
  }

  const itemClasses = [
    'oracle-sidebar-item',
    chatSessionId && 'oracle-sidebar-item--linkable',
    isSelected && 'oracle-sidebar-item--selected',
    isContextMenuActive && 'oracle-sidebar-item--context-menu-active',
    !isTouchable && 'oracle-sidebar-item--not-touchable',
    className,
  ].filter(Boolean).join(' ')

  const getAriaLabel = (key: string, params: Record<string, string> = {}) => {
    if (!staticText || !staticText.getIn) return key
    const template = staticText.getIn(['sidebarItem', key], key) as string
    return template.replace(/{(\w+)}/g, (match: string, param: string) => params[param] || match)
  }

  // If we have a chatSessionId, render as wrapper div with Link and context button
  if (chatSessionId) {
    return (
      <div
        className={itemClasses}
      >
        <Link
          to={URL_AI_CHAT}
          query={newQuery}
          className="oracle-sidebar-item__link"
          style={{ textDecoration: 'none' }}
          onClick={handleClick}
          role="button"
          aria-selected={isSelected}
          aria-label={getAriaLabel('select', { title })}
          scrollToTop={false}
        >
          {isRenaming ? (
            <input
              ref={inputRef}
              type="text"
              value={renameState?.newName || ''}
              onChange={handleRenameInputChange}
              onKeyDown={handleRenameKeyDown}
              onBlur={handleRenameBlur}
              className="oracle-sidebar-item__rename-input"
              maxLength={100}
              aria-label={getAriaLabel('renameConversation')}
            />
          ) : (
            <span className="oracle-sidebar-item__title">
              {titleHtml ? (
                <span dangerouslySetInnerHTML={{ __html: titleHtml }} />
              ) : (
                title
              )}
            </span>
          )}
        </Link>
        <button
          className="oracle-sidebar-item__context-menu"
          onClick={handleContextMenuClick}
          onTouchStart={handleContextMenuTouchStart}
          onTouchEnd={handleContextMenuTouchEnd}
          tabIndex={0}
          aria-label={getAriaLabel('moreOptions')}
        >
          <Icon color="currentColor" size={24}>{makeIcon('dots-horiz')}</Icon>
        </button>
      </div>
    )
  }

  // Fallback for items without chatSessionId - render as div
  return (
    <div
      ref={itemRef}
      className={itemClasses}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-selected={isSelected}
      aria-label={getAriaLabel('select', { title })}
    >
      {isSelected && (
        <Icon color="currentColor" size={24}>
          {makeIcon('selection-dot')}
        </Icon>
      )}
      {personalityId && (
        <PersonalityIcon
          personalityId={personalityId}
          width={20}
          height={20}
          className="oracle-sidebar-item__personality-icon"
        />
      )}
      <span className="oracle-sidebar-item__title">
        {titleHtml ? (
          <span dangerouslySetInnerHTML={{ __html: titleHtml }} />
        ) : (
          title
        )}
      </span>
      <button
        className="oracle-sidebar-item__context-menu"
        onClick={handleContextMenuClick}
        onTouchStart={handleContextMenuTouchStart}
        onTouchEnd={handleContextMenuTouchEnd}
        tabIndex={0}
        aria-label={getAriaLabel('moreOptions')}
      >
        <Icon color="currentColor" size={24}>{makeIcon('dots-horiz')}</Icon>
      </button>
    </div>
  )
}
